// ** React Imports
import { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { setMainMediaInfo, setModalEditedContent } from "store/modules/modal";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import { ActionButton } from "components/layout/ActionButton";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import pseudoFormat from "utils/pseudoFormat";

// ** Custom Hooks
import useUpdateEffect from "hooks/useUpdateEffect";

// ** Contexts
import authContext from "contexts/authContext";

// #####################################################

const InteractionsInModal = ({
	userId,
	id,
	type,
	canLike,
	likes = 0,
	isLiked = false,
	comments = 0,
	isCommented = false,
	onCommentClick = null,
	isPhone,
	isTablet,
	alwaysDark = false,
	showCommentsSection = false,
	visibleEditor,
}) => {
	const suspension = useSelector((state) => state.user?.suspension);
	const isBan = suspension?.duration > 0;
	const { t } = useTranslation(["common", "suspensions", "pages"]);

	const history = useHistory();
	const dispatch = useDispatch();

	const [disabledLike, setDisabledLike] = useState(false);

	const [_likes, _setLikes] = useState(likes);
	const [_isLiked, _setIsLiked] = useState(isLiked);

	const [_comments, _setComments] = useState(comments);
	const [_isCommented, _setIsCommented] = useState(isCommented);

	useUpdateEffect(() => {
		_setLikes(likes);
		_setIsLiked(isLiked);
		_setComments(comments);
		_setIsCommented(isCommented);
	}, [id]);

	const _onLikeClick = useCallback(() => {
		setDisabledLike(true);
		if (_isLiked) {
			api.delete(`/like?user=${userId}&id=${id}&type=${type}`)
				.then(() => {
					_setLikes(_likes > 0 ? _likes - 1 : 0);
					_setIsLiked(false);

					dispatch(
						setMainMediaInfo({
							likesCount: _likes > 0 ? _likes - 1 : 0,
							contentId: id || null,
						})
					);
				})
				.catch((e) => {
					if (e?.response?.data?.code === 99) {
						_setLikes(_likes > 0 ? _likes - 1 : 0);
						_setIsLiked(false);

						dispatch(
							setMainMediaInfo({
								likesCount: _likes > 0 ? _likes - 1 : 0,
								contentId: id || null,
							})
						);
					}
					toast.error(e.response.data.error);
				})
				.finally(() => {
					setDisabledLike(false);
				});
		} else {
			api.put(`/like?user=${userId}&id=${id}&type=${type}`)
				.then(() => {
					_setIsLiked(true);
					_setLikes(_likes + 1);
					dispatch(
						setMainMediaInfo({
							likesCount: _likes + 1,
							contentId: id || null,
						})
					);
				})
				.catch((e) => {
					if (e?.response?.data?.code === 99) {
						_setIsLiked(true);
						_setLikes(_likes + 1);
						dispatch(
							setMainMediaInfo({
								likesCount: _likes + 1,
								contentId: id || null,
							})
						);
					}
					toast.error(e.response.data.error);
				})
				.finally(() => {
					setDisabledLike(false);
				});
		}
		dispatch(setModalEditedContent(id));
	}, [isLiked, _isLiked, userId, id, type]);

	useEffect(() => {
		_setLikes(likes);
	}, [likes]);
	useEffect(() => {
		_setIsLiked(isLiked);
	}, [isLiked]);
	useEffect(() => {
		_setComments(comments);
	}, [comments]);
	useEffect(() => {
		_setIsCommented(isCommented);
	}, [isCommented]);

	useEffect(() => {
		_setIsCommented(isCommented);
	}, []);

	const isAuth = useContext(authContext);

	const tooltipLikeText = () => {
		switch (type) {
			case "photo":
				return "canNotLikePhoto";
			case "video":
				return "canNotLikeVideo";
			case "post":
				return "canNotLikePost";
			default:
				return "";
		}
	};

	return (
		<Block flex>
			<ActionButton
				alwaysDark={alwaysDark}
				isHeart
				onClick={(event) => {
					if (isAuth) {
						_onLikeClick(event);
					} else {
						history.push("/");
						window.scrollTo(0, 0);
					}
				}}
				disabled={!canLike || disabledLike || isBan}
				noColorDisabled={disabledLike}
				liked={_isLiked}
				label={_likes > 0 ? pseudoFormat(_likes) : null}
				customSvgSize={"21px"}
				customMargin={"0 0 0 0"}
				tooltipInfo={
					isBan
						? t("suspensions:bannedAccount")
						: canLike
						? false
						: t(tooltipLikeText())
				}
				tooltipPlacement={{
					anchor: "RIGHT_CENTER",
				}}
			/>
			<ActionButton
				alwaysDark={alwaysDark}
				isComment
				onClick={(event) => {
					if (onCommentClick) {
						onCommentClick({
							id,
							type,
							originalEvent: event,
						});
					}
				}}
				commented={_isCommented}
				label={_comments > 0 ? pseudoFormat(_comments) : null}
				customSvgSize={"21px"}
				tooltipPlacement={{
					anchor: "RIGHT_CENTER",
				}}
				isActive={
					(showCommentsSection && (isPhone || isTablet)) ||
					(visibleEditor?.type === "ADD_COMMENT" &&
						visibleEditor?.id === "modal" &&
						!isPhone &&
						!isTablet)
				}
				noHover={isPhone || isTablet}
			/>
		</Block>
	);
};

// #####################################################

export default InteractionsInModal;

// #####################################################
